import { useState } from "react";
import { DEFAULT_GP_TARGET, DEFAULT_TIMEZONE, isGpTarget, ROLLOVER_HOURS, SERVICE_ROLLOVER_HOUR, WEEK_STARTS_ON } from "@baros/core";
import type { OrgVenue, VenueBody } from "./api.js";

/**
 * The zones a bar is likely to be in. A venue keeps any valid zone it was
 * given; these are what the editor offers.
 */
const TIMEZONES: readonly { readonly zone: string; readonly label: string }[] = [
  { zone: "Australia/Melbourne", label: "Melbourne (VIC)" },
  { zone: "Australia/Sydney", label: "Sydney (NSW, ACT)" },
  { zone: "Australia/Brisbane", label: "Brisbane (QLD)" },
  { zone: "Australia/Adelaide", label: "Adelaide (SA)" },
  { zone: "Australia/Hobart", label: "Hobart (TAS)" },
  { zone: "Australia/Darwin", label: "Darwin (NT)" },
  { zone: "Australia/Perth", label: "Perth (WA)" },
  { zone: "Pacific/Auckland", label: "Auckland (NZ)" },
];

const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

/** `3` → `3 am`, `0` → `midnight`. */
const hourLabel = (h: number) => (h === 0 ? "midnight" : `${h} am`);

interface Props {
  readonly venues: readonly OrgVenue[];
  readonly busy: string | null;
  readonly onCreate: (body: VenueBody) => Promise<void>;
  readonly onSave: (venueId: string, body: VenueBody) => Promise<void>;
}

const EMPTY: VenueBody = { name: "", suburb: "", state: "", seats: 0 };

/**
 * The Venues tab.
 *
 * A venue id is derived from its name once, at creation, and then never
 * changes — it is in the sort key of every count, order and shift that has
 * ever happened there. Renaming a venue changes the name on both rows and
 * leaves the id alone, which the id line under each name says out loud.
 */
export function Venues(p: Props) {
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState<VenueBody>(EMPTY);
  const [editing, setEditing] = useState<string | null>(null);
  const [edit, setEdit] = useState<VenueBody>(EMPTY);

  return (
    <>
      <div className="org-section">
        <h3>Venues · {p.venues.length}</h3>
        {p.venues.length === 0 && (
          <div className="org-note">
            No venues yet. Stock, prep, wastage, orders and staff all hang off a venue, so this
            is the first thing to add.
          </div>
        )}

        {p.venues.map((v) =>
          editing === v.venueId ? (
            <div className="org-card" key={v.venueId}>
              <VenueFields value={edit} onChange={setEdit} />
              <div className="row">
                <span className="org-foot-note">{v.venueId}</span>
                <span className="spacer" />
                <button className="btn" onClick={() => setEditing(null)} disabled={p.busy === v.venueId}>
                  Cancel
                </button>
                <button
                  className="btn primary"
                  disabled={p.busy === v.venueId || !edit.name.trim() || (edit.gpTarget !== undefined && !isGpTarget(edit.gpTarget))}
                  onClick={async () => {
                    await p.onSave(v.venueId, edit);
                    setEditing(null);
                  }}
                >
                  {p.busy === v.venueId ? "Saving…" : "Save venue"}
                </button>
              </div>
            </div>
          ) : (
            <div className="table" key={v.venueId}>
              <div className="trow org-trow">
                <span className="m-name">
                  <span className="n">{v.name}</span>
                  <span className="s">
                    {[v.suburb, v.state].filter(Boolean).join(" · ") || "no address"} · {v.venueId}
                  </span>
                </span>
                <span className="c-build">{v.seats ? `${v.seats} seats` : "seats not set"}</span>
                <span className="c-build">
                  {v.staffCount} {v.staffCount === 1 ? "person" : "people"}
                </span>
                <span className="m-act">
                  <button
                    className="btn sm"
                    onClick={() => {
                      setEditing(v.venueId);
                      setEdit({
                        name: v.name,
                        suburb: v.suburb,
                        state: v.state,
                        seats: v.seats,
                        guestPrices: v.guestPrices !== false,
                        timezone: v.timezone ?? DEFAULT_TIMEZONE,
                        rolloverHour: v.rolloverHour ?? SERVICE_ROLLOVER_HOUR,
                        weekStartsOn: v.weekStartsOn ?? WEEK_STARTS_ON,
                        gpTarget: v.gpTarget ?? DEFAULT_GP_TARGET,
                      });
                    }}
                  >
                    Edit
                  </button>
                </span>
              </div>
            </div>
          ),
        )}
      </div>

      <div className="org-section">
        <h3>Add a venue</h3>
        {!creating ? (
          <div>
            <button className="btn dashed" onClick={() => { setCreating(true); setDraft(EMPTY); }}>
              + New venue
            </button>
          </div>
        ) : (
          <div className="org-card dashed">
            <VenueFields value={draft} onChange={setDraft} />
            <div className="org-note">
              Nobody is assigned to a new venue, including you. Give people access on the Members
              tab — an org role alone does not put anyone on the floor.
            </div>
            <div className="row">
              <span className="spacer" />
              <button className="btn" onClick={() => setCreating(false)} disabled={p.busy === "venue"}>
                Cancel
              </button>
              <button
                className="btn primary"
                disabled={p.busy === "venue" || !draft.name.trim()}
                onClick={async () => {
                  await p.onCreate(draft);
                  setCreating(false);
                  setDraft(EMPTY);
                }}
              >
                {p.busy === "venue" ? "Creating…" : "Create the venue"}
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

function VenueFields(props: {
  readonly value: VenueBody;
  readonly onChange: (v: VenueBody) => void;
}) {
  const { value, onChange } = props;
  return (
    <div className="org-grid3">
      <label className="field">
        <span>Name</span>
        <input
          type="text"
          value={value.name}
          placeholder="Above Board"
          onChange={(e) => onChange({ ...value, name: e.target.value })}
        />
      </label>
      <label className="field">
        <span>Suburb</span>
        <input
          type="text"
          value={value.suburb}
          placeholder="Collingwood"
          onChange={(e) => onChange({ ...value, suburb: e.target.value })}
        />
      </label>
      <label className="field">
        <span>State</span>
        <input
          type="text"
          value={value.state}
          placeholder="VIC"
          onChange={(e) => onChange({ ...value, state: e.target.value })}
        />
      </label>
      <label className="field">
        <span>Seats</span>
        <input
          className="num-input"
          type="number"
          min={0}
          step={1}
          value={value.seats}
          onChange={(e) => onChange({ ...value, seats: Math.max(0, Math.round(Number(e.target.value) || 0)) })}
        />
      </label>
      {value.timezone !== undefined && (
        // The venue's clock: every "tonight", "this week" and time on its
        // screens is decided on it, wherever the person looking is.
        <>
          <label className="field">
            <span>Timezone</span>
            <select value={value.timezone} onChange={(e) => onChange({ ...value, timezone: e.target.value })}>
              {!TIMEZONES.some((t) => t.zone === value.timezone) && <option value={value.timezone}>{value.timezone}</option>}
              {TIMEZONES.map((t) => (
                <option key={t.zone} value={t.zone}>{t.label}</option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Day ends at</span>
            <select
              value={value.rolloverHour ?? SERVICE_ROLLOVER_HOUR}
              onChange={(e) => onChange({ ...value, rolloverHour: Number(e.target.value) })}
            >
              {ROLLOVER_HOURS.map((h) => (
                <option key={h} value={h}>{hourLabel(h)}{h === SERVICE_ROLLOVER_HOUR ? " (default)" : ""}</option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Week starts on</span>
            <select
              value={value.weekStartsOn ?? WEEK_STARTS_ON}
              onChange={(e) => onChange({ ...value, weekStartsOn: Number(e.target.value) })}
            >
              {WEEKDAYS.map((d, i) => (
                <option key={d} value={i}>{d}{i === WEEK_STARTS_ON ? " (default)" : ""}</option>
              ))}
            </select>
          </label>
        </>
      )}
      {value.gpTarget !== undefined && (
        // What Menu engineering and the dashboard hold every drink to.
        <label className="field">
          <span>Gross profit target %</span>
          <input
            type="number"
            min={1}
            max={99}
            step={0.5}
            value={Number.isFinite(value.gpTarget) ? value.gpTarget : ""}
            onChange={(e) => onChange({ ...value, gpTarget: e.target.value === "" ? Number.NaN : Number(e.target.value) })}
          />
        </label>
      )}
      {value.guestPrices !== undefined && (
        // The guest page a queue text links to (#126) is a public menu; a bar
        // may not want a scrapeable price list.
        <label className="row tablet-toggle" style={{ gap: 10 }}>
          <input
            type="checkbox"
            checked={value.guestPrices}
            onChange={(e) => onChange({ ...value, guestPrices: e.target.checked })}
          />
          <span>Show prices on the guest page</span>
        </label>
      )}
    </div>
  );
}
